import { Component, HostBinding, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  StructuredDisplayLine,
  UtilsService,
} from '../../../../services/utils/utils.service';

const TITLE_KEYS = new Set([
  'name',
  'upi_id',
  'upi id',
  'vpa',
  'username',
  'full_name',
  'full name',
]);

const TOKEN_KEYS = new Set([
  'balance token',
  'balance_token',
  'token',
  'hash',
]);

const UPI_KEYS = new Set(['upi_id', 'upi id', 'vpa']);

@Component({
  selector: 'app-abacus-structured-value',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './abacus-structured-value.component.html',
  styleUrl: './abacus-structured-value.component.css',
})
export class AbacusStructuredValueComponent {
  lines: StructuredDisplayLine[] = [];
  hasPairs = false;
  isRecord = false;
  /** Two or more fields (for example name + subscriber id) share the dark record box. */
  isPanel = false;
  /** UPI / name+token rows: primary + secondary values, no repeated labels. */
  isCompact = false;
  compactPrimary = '';
  compactSecondary = '';
  compactSecondaryIsToken = false;

  /** Name + UPI ID pair: host class so the parent list can stretch the card. */
  @HostBinding('class.is-pair-card')
  get pairCardHost(): boolean {
    return this.isCompact && !this.compactSecondaryIsToken;
  }

  /** A single plain value (icon URL, website URL) stays beside the field label. */
  @HostBinding('class.is-inline-value')
  get inlineValueHost(): boolean {
    return (
      !this.isCompact &&
      !this.isPanel &&
      !this.isRecord &&
      !this.hasPairs &&
      this.lines.length <= 1
    );
  }

  constructor(private readonly utilsService: UtilsService) { }

  @Input() set text(value: string | null | undefined) {
    this.lines = this.utilsService.parseStructuredDisplayText(value);
    this.hasPairs = this.lines.some((line) => line.kind === 'pair');
    const pairs = this.lines.filter(
      (line): line is Extract<StructuredDisplayLine, { kind: 'pair' }> =>
        line.kind === 'pair',
    );
    this.isRecord = pairs.length >= 3;
    this.applyCompactMode(pairs);
    this.isPanel = !this.isCompact && pairs.length >= 2;

    // Lone "City: …" / "Status: …" under an existing field label — show value only.
    // Do not inject an extra key label into the UI.
    if (!this.isCompact && !this.isRecord && pairs.length === 1 && this.lines.length === 1) {
      this.lines = [{ kind: 'plain', value: pairs[0].value }];
      this.hasPairs = false;
    }
  }

  formatKey(key: string): string {
    return this.utilsService.formatStructuredDisplayKey(key);
  }

  isTitleLine(line: StructuredDisplayLine, index: number): boolean {
    if (!this.isRecord || line.kind !== 'pair' || index !== 0) {
      return false;
    }
    return TITLE_KEYS.has(line.key.trim().toLowerCase());
  }

  isTokenLine(line: StructuredDisplayLine): boolean {
    if (line.kind !== 'pair') {
      return false;
    }
    return TOKEN_KEYS.has(line.key.trim().toLowerCase());
  }

  private applyCompactMode(
    pairs: Array<Extract<StructuredDisplayLine, { kind: 'pair' }>>,
  ): void {
    this.isCompact = false;
    this.compactPrimary = '';
    this.compactSecondary = '';
    this.compactSecondaryIsToken = false;

    if (pairs.length === 2) {
      const upi = pairs.find((p) => UPI_KEYS.has(p.key.trim().toLowerCase()));
      const name = pairs.find((p) => p.key.trim().toLowerCase() === 'name');
      if (upi && name) {
        this.isCompact = true;
        this.compactPrimary = name.value;
        this.compactSecondary = upi.value;
        return;
      }
    }

    // "Himender .: <token>" balance-token list items
    if (pairs.length === 1) {
      const pair = pairs[0];
      if (this.looksLikeToken(pair.value)) {
        this.isCompact = true;
        this.compactPrimary = pair.key;
        this.compactSecondary = pair.value;
        this.compactSecondaryIsToken = true;
      }
    }
  }

  private looksLikeToken(value: string): boolean {
    const trimmed = String(value || '').trim();
    return trimmed.length >= 24 && /^[A-Za-z0-9+/=_-]+$/.test(trimmed);
  }
}
